import { Button } from '@/components/shared/shadcn-ui/button';
import { DialogFooter } from '@/components/shared/shadcn-ui/dialog';
import { Input } from '@/components/shared/shadcn-ui/input';
import { Label } from '@/components/shared/shadcn-ui/label';
import { Switch } from '@/components/shared/shadcn-ui/switch';
import { Alert } from '@/components/shared/alert';
import { DefaultSubstituteFoodCombobox } from '@/components/restaurant/track-locations/default-substitute-food-combobox';
import { isNotEmpty } from '@/utils/helpers';
import {
  useAuthUserRestaurant,
  useCreateRestaurantTrackLocationMutation,
  ValidationError,
} from '@durma-soft/gros-sdk';
import { FormEvent, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

interface RestaurantTrackLocationFormData {
  name: string;
  street: string;
  city: string;
  zip: string;
  state: string;
  country: string;
  enable_substitute_food_selection: boolean;
  default_substitute_food_id: number | null;
}

interface CreateRestaurantTrackLocationProps {
  closeModal: () => unknown;
}

export const CreateRestaurantTrackLocationForm = ({
  closeModal,
}: CreateRestaurantTrackLocationProps) => {
  const uid = useId();

  const { t } = useTranslation();

  const [formData, setFormData] = useState<RestaurantTrackLocationFormData>({
    name: '',
    street: '',
    city: '',
    zip: '',
    state: '',
    country: '',
    enable_substitute_food_selection: true,
    default_substitute_food_id: null,
  });

  const restaurant = useAuthUserRestaurant();

  const createRestaurantTrackLocation =
    useCreateRestaurantTrackLocationMutation();

  const isValid = useMemo(() => {
    return isNotEmpty(formData.name);
  }, [formData.name]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isValid) return;
    try {
      await createRestaurantTrackLocation.mutateAsync({
        restaurant_id: restaurant.id,
        name: formData.name,
        street: formData.street || undefined,
        city: formData.city || undefined,
        zip: formData.zip || undefined,
        state: formData.state || undefined,
        country: formData.country || undefined,
        enable_substitute_food_selection:
          formData.enable_substitute_food_selection,
        default_substitute_food_id: formData.default_substitute_food_id,
      });
      closeModal();
      toast.success(t('track-location.create-success-msg'));
    } catch (error) {
      if (error instanceof ValidationError) {
        return toast.error(error.message);
      }
      toast.error(t('track-location.create-error-msg'));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full flex flex-col gap-6">
      <div className="grid gap-4">
        {/* Name */}
        <div className="flex flex-col gap-2">
          <Label htmlFor={uid + '-name'}>{t('common.name')}</Label>
          <Input
            required
            id={uid + '-name'}
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder={t('common.name-placeholder')}
          />
        </div>
        {/* Street */}
        <div className="flex flex-col gap-2">
          <Label htmlFor={uid + '-street'}>
            {t('common.address-details.street')}
          </Label>
          <Input
            id={uid + '-street'}
            value={formData.street || ''}
            onChange={(e) =>
              setFormData({ ...formData, street: e.target.value })
            }
            placeholder={t('common.address-details.street-placeholder')}
          />
        </div>
        {/* City */}
        <div className="flex flex-col gap-2">
          <Label htmlFor={uid + '-city'}>
            {t('common.address-details.city')}
          </Label>
          <Input
            id={uid + '-city'}
            value={formData.city || ''}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
            placeholder={t('common.address-details.city-placeholder')}
          />
        </div>
        {/* ZIP */}
        <div className="flex flex-col gap-2">
          <Label htmlFor={uid + '-zip'}>
            {t('common.address-details.zip')}
          </Label>
          <Input
            id={uid + '-zip'}
            value={formData.zip || ''}
            onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
            placeholder={t('common.address-details.zip-placeholder')}
          />
        </div>
        {/* State */}
        <div className="flex flex-col gap-2">
          <Label htmlFor={uid + '-state'}>
            {t('common.address-details.state')}
          </Label>
          <Input
            id={uid + '-state'}
            value={formData.state || ''}
            onChange={(e) =>
              setFormData({ ...formData, state: e.target.value })
            }
            placeholder={t('common.address-details.state-placeholder')}
          />
        </div>
        {/* Country */}
        <div className="flex flex-col gap-2">
          <Label htmlFor={uid + '-country'}>
            {t('common.address-details.country')}
          </Label>
          <Input
            id={uid + '-country'}
            value={formData.country || ''}
            onChange={(e) =>
              setFormData({ ...formData, country: e.target.value })
            }
            placeholder={t('common.address-details.country-placeholder')}
          />
        </div>
        {/* Enable substitute food selection */}
        <div className="flex flex-col gap-2">
          <Label htmlFor={uid + '-enable-substitute-food-selection'}>
            {t('track-location.enable-substitute-food-selection')}
          </Label>
          <Switch
            id={uid + '-enable-substitute-food-selection'}
            checked={formData.enable_substitute_food_selection}
            onCheckedChange={(checked) =>
              setFormData({
                ...formData,
                enable_substitute_food_selection: Boolean(checked),
                default_substitute_food_id: null,
              })
            }
          />
        </div>
        {/* Default substitute food */}
        {!formData.enable_substitute_food_selection && (
          <div className="flex flex-col gap-2">
            <Label htmlFor={uid + '-default-substitute-food'}>
              {t('track-location.default-substitute-food')}
            </Label>
            <DefaultSubstituteFoodCombobox
              id={uid + '-default-substitute-food'}
              restaurantId={restaurant.id}
              value={formData.default_substitute_food_id}
              onChange={(foodId) =>
                setFormData({ ...formData, default_substitute_food_id: foodId })
              }
            />
            {!formData.default_substitute_food_id && (
              <Alert
                variant="destructive"
                title={t('common.warning')}
                description={t(
                  'track-location.no-default-substitute-food-warning',
                )}
              />
            )}
          </div>
        )}
      </div>
      <DialogFooter>
        <Button
          type="submit"
          disabled={!isValid}
          isLoading={createRestaurantTrackLocation.isPending}
        >
          {t('track-location.create')}
        </Button>
      </DialogFooter>
    </form>
  );
};
